import DOMPurify from 'isomorphic-dompurify';
import { phone } from 'phone';
import { z } from 'zod';

/**
 * The version of this contact form schema.
 * The client includes the version in the form body for debugging purposes.
 */
export const CONTACT_FORM_VERSION: string = '4.0.15';

/**
 * Catch-all HubSpot Marketing Campaign GUID when a form has no `hubspotCampaignId`
 * (legacy submissions, non-Sanity forms, footer newsletter before CMS backfill, etc.).
 *
 * Update this when the default routing campaign changes.
 */
export const DEFAULT_HUBSPOT_CAMPAIGN_ID =
  '079d6849-eec6-4d98-a3ad-2f699437d1ba';

/**
 * HubSpot "Marketing Information" email subscription type ID.
 *
 * Used as the default email subscription for forms: the Sanity field's
 * initial value, and the client-side fallback when a form document has no
 * `hubspotEmailSubscriptionIds` value set (null/undefined — an explicitly
 * empty selection is respected).
 */
export const HUBSPOT_MARKETING_INFORMATION_SUBSCRIPTION_ID = '2569728161';

/**
 * Sanitize inputs to prevent XSS
 * @see https://cheatsheetseries.owasp.org/cheatsheets/Cross_Site_Scripting_Prevention_Cheat_Sheet.html
 */
export const sanitizeString = (value: string) => DOMPurify.sanitize(value);

/** Max character lengths for form fields */
export const MAX_LENGTH = {
  text: 100,
  email: 300,
  message: 3000,
} as const;

const maxLengthError = (field: string, max: number) =>
  `${field} must be ${max} characters or fewer.`;

/**
 * Company sizes options - used to populate the company size select field
 */
export const CompanySizesOptions = [
  'Under 500 employees',
  '500 to 2000 employees',
  '2000 to 5000 employees',
  'Above 5000 employees',
] as const;

/**
 * Partner Types Options - used to populate the partner type select field
 */
export const PartnerTypesOptions = [
  'Technology Partner',
  'Solutions Partner',
  'Other',
] as const;

/**
 * HubSpot `transcend_last_touch_converting_form_type` picklist values.
 * Configured per-form in Sanity; synced to HubSpot (and then Salesforce).
 */
export const HubspotConvertingFormTypeOptions = [
  'resources',
  'lead_contact_form',
  'newsletter',
  'event_live',
  'event_virtual',
] as const;

export type HubspotConvertingFormType =
  (typeof HubspotConvertingFormTypeOptions)[number];

/**
 * Product areas a contact form submitter is exploring.
 * Select-all-that-apply follow-up on the contact page.
 */
export const BusinessNeedOptions = [
  'Agent controls',
  'AI Governance',
  'Policy Engine',
  'Consent & Preference Management',
  'DSR Automation',
  'Privacy Center',
  'Cookie Banner',
] as const;

export type BusinessNeed = (typeof BusinessNeedOptions)[number];

/**
 * Whether the submitter has a funded initiative or is only exploring.
 */
export const BusinessNeedIntentOptions = [
  'I have a specific initiative with budget that needs encoded governance or data decisioning',
  'I am just exploring to learn more about governance and data decisioning',
] as const;

export type BusinessNeedIntent = (typeof BusinessNeedIntentOptions)[number];

/**
 * ChiliPiper meeting captured from Concierge `onSuccess` / booking messages.
 * Attached to later contact-form upserts for the same submission.
 */
export const ChiliPiperMeeting = z.object({
  eventId: z.string(),
  /** Salesforce user id from Concierge (`assigneeId`). */
  assignee: z.string().transform(sanitizeString).optional(),
  /** Assignee email from webhook / Concierge when present. */
  assigneeEmail: z.string().transform(sanitizeString).optional(),
});
export type ChiliPiperMeeting = z.infer<typeof ChiliPiperMeeting>;

/** Sanity / Studio labels for {@link HubspotConvertingFormTypeOptions}. */
export const HubspotConvertingFormTypeList = [
  { title: 'Gated Content / Resource download', value: 'resources' },
  { title: 'Lead / Demo / Contact Form', value: 'lead_contact_form' },
  { title: 'Newsletter', value: 'newsletter' },
  { title: 'Live Events', value: 'event_live' },
  { title: 'Virtual Events', value: 'event_virtual' },
] as const satisfies ReadonlyArray<{
  title: string;
  value: HubspotConvertingFormType;
}>;
/**
 * The fields that are part of the contact form itself.
 * This can be used to validate ALL contact forms, even if they omit some of the optional fields.
 */
export const ContactFormFields = z.object({
  /** The submitter's first name */
  firstName: z
    .string()
    .max(MAX_LENGTH.text, {
      error: maxLengthError('First name', MAX_LENGTH.text),
    })
    .transform(sanitizeString)
    .optional(),

  /** The submitter's last name */
  lastName: z
    .string()
    .max(MAX_LENGTH.text, {
      error: maxLengthError('Last name', MAX_LENGTH.text),
    })
    .transform(sanitizeString)
    .optional(),

  /** The submitter's work email */
  email: z.email().max(MAX_LENGTH.email, {
    error: maxLengthError('Email', MAX_LENGTH.email),
  }),

  /** The submitter's job title */
  title: z
    .string()
    .max(MAX_LENGTH.text, {
      error: maxLengthError('Job title', MAX_LENGTH.text),
    })
    .transform(sanitizeString)
    .optional(),

  /** The submitter's company name */
  company: z
    .string()
    .max(MAX_LENGTH.text, { error: maxLengthError('Company', MAX_LENGTH.text) })
    .transform(sanitizeString)
    .optional(),

  /** The company's employee count */
  companySize: z.enum(CompanySizesOptions).optional(),

  /** The submitter's phone number */
  phone: z
    .string()
    .optional()
    .refine(
      (val) => {
        // If the value is undefined (optional), it's valid.
        // Otherwise, check if it's a valid phone number.
        return val === undefined || phone(val).isValid;
      },
      {
        error: 'Must be a valid phone number.',
      },
    ),

  /** The submitter's country of residence */
  country: z
    .string()
    .max(MAX_LENGTH.text, { error: maxLengthError('Country', MAX_LENGTH.text) })
    .transform(sanitizeString)
    .optional(),

  /** The submitter's state of residence */
  state: z
    .string()
    .max(MAX_LENGTH.text, { error: maxLengthError('State', MAX_LENGTH.text) })
    .transform(sanitizeString)
    .optional(),

  /** Any text that the submitter sent with this submission */
  message: z
    .string()
    .max(MAX_LENGTH.message, {
      error: maxLengthError('Message', MAX_LENGTH.message),
    })
    .transform(sanitizeString)
    .optional(),

  /** If this is a partner contacting us */
  partnerType: z.enum(PartnerTypesOptions).optional(),

  /** If this is a partner submitting a referral */
  referralPartner: z
    .object({
      partnerFirstName: z
        .string()
        .max(MAX_LENGTH.text, {
          error: maxLengthError('First name', MAX_LENGTH.text),
        })
        .transform(sanitizeString),
      partnerLastName: z
        .string()
        .max(MAX_LENGTH.text, {
          error: maxLengthError('Last name', MAX_LENGTH.text),
        })
        .transform(sanitizeString),
      partnerEmail: z.email().max(MAX_LENGTH.email, {
        error: maxLengthError('Email', MAX_LENGTH.email),
      }),
      partnerCompany: z
        .string()
        .max(MAX_LENGTH.text, {
          error: maxLengthError('Company', MAX_LENGTH.text),
        })
        .transform(sanitizeString),
    })
    .optional(),

  /**
   * The submitter's consent to be contacted. If the user is requesting a demo (i.e. be contacted), this is `true` as implied consent.
   * This is only `false` when we're using this form for another purpose on the marketing website, such as to send swag / unlock content downloads.
   * In such a case, we can also request consent for marketing in that contact form.
   */
  consent: z.union([z.boolean(), z.enum(['true', 'false'])]),

  /** The submission ID that is returned from postgres. If this is a multi-step form, it is expected in subsequent submissions */
  submissionId: z.string().optional(),

  /**
   * Product areas the submitter is exploring.
   * Contact-page follow-up; select all that apply.
   */
  businessNeeds: z.array(z.enum(BusinessNeedOptions)).optional(),

  /** Whether the submitter has a funded initiative or is only exploring */
  businessNeedIntent: z.enum(BusinessNeedIntentOptions).optional(),

  /** ChiliPiper meeting booked during this contact flow, if any */
  chiliPiperMeeting: ChiliPiperMeeting.optional(),

  /**
   * Rails waitlist — primary agent use cases (select all that apply).
   * Synced to HubSpot `primary_agent_use_case`.
   */
  primaryUseCases: z.array(z.string().transform(sanitizeString)).optional(),

  /**
   * Rails waitlist — number of agents to control.
   * Synced to HubSpot `number_of_agents`. Accepts string (form input) or number.
   */
  agentCount: z.union([z.string(), z.number()]).optional(),

  /**
   * Rails waitlist — current agent/data control approaches.
   * Synced to HubSpot `current_agent_control_approach`.
   */
  controlApproaches: z.array(z.string().transform(sanitizeString)).optional(),

  /**
   * Rails waitlist — agent / data platforms used.
   * Synced to HubSpot `agent___data_platform_used`.
   */
  platforms: z.array(z.string().transform(sanitizeString)).optional(),

  /**
   * Rails waitlist — estimated monthly agent spend.
   * Synced to HubSpot `estimated_monthly_agent_spend`.
   */
  monthlySpend: z
    .string()
    .max(MAX_LENGTH.text, {
      error: maxLengthError('Monthly spend', MAX_LENGTH.text),
    })
    .transform(sanitizeString)
    .optional(),

  /**
   * Rails waitlist — business purposes.
   * Synced to HubSpot `business_purpose`.
   */
  businessPurposes: z.array(z.string().transform(sanitizeString)).optional(),
});
export type ContactFormFields = z.infer<typeof ContactFormFields>;

/**
 * Metadata that is included in the form body, but is not part of the form component itself.
 */
export const ContactFormMetadata = z.object({
  /** Which version of the form the client is on */
  formVersion: z
    .string()
    .regex(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/)
    .optional(),

  /** The reCAPTCHA token (only for use on our marketing website) */
  recaptchaToken: z
    .string()
    .min(50)
    // alphanumeric or underscore or hyphen
    .regex(/^(\w|-)+$/)
    .optional(),

  /** Segment tracking event configured in Sanity for this form */
  trackingEventName: z.string().transform(sanitizeString).optional(),

  /** CMS form title — used for HubSpot form_title property */
  formTitle: z.string().transform(sanitizeString).optional(),

  /**
   * Gated content title for outbound email variables — configured on resource
   * forms in Sanity (`contentPieceTitle`).
   */
  contentPieceTitle: z.string().transform(sanitizeString).optional(),

  /**
   * HubSpot Marketing Campaign GUID from Sanity — used for workflow / campaign handoff.
   * Example: ddfc857f-941b-4e1b-b58e-36780a898a12
   */
  hubspotCampaignId: z.string().uuid().transform(sanitizeString).optional(),

  /**
   * HubSpot last-touch converting form type from Sanity.
   * Maps to HubSpot contact property `transcend_last_touch_converting_form_type`
   * (picklist values must match {@link HubspotConvertingFormTypeOptions}).
   */
  hubspotConvertingFormType: z
    .enum(HubspotConvertingFormTypeOptions)
    .optional(),

  /**
   * Comma and space-separated string of HubSpot email subscription type IDs, e.g.: `2569728161, 2933598087`
   * Configured per-form in Sanity. When absent, the backend opts the contact into its default subscription types.
   */
  hubspotEmailSubscriptionIds: z
    .string()
    .regex(/^(\d+)(,\s*\d+)*$/)
    .optional(),

  /** The user's existing anonymousId from Segment */
  anonymousId: z.string().transform(sanitizeString).optional(),

  /** Which channel this traffic is coming from. In `main` this'll likely always be "Admin Dashboard" */
  utm_source: z.string().transform(sanitizeString).optional(),

  /** What type of channel this traffic is coming from. */
  utm_medium: z.string().transform(sanitizeString).optional(),

  /** Which marketing campaign this is coming from. */
  utm_campaign: z.string().transform(sanitizeString).optional(),

  /** A unique ID */
  utm_id: z.string().transform(sanitizeString).optional(),

  /** Only relevant for search campaigns (e.g. what google search term they came from) */
  utm_term: z.string().transform(sanitizeString).optional(),

  /** Only relevant for content-based campaigns (e.g. what blog post they were on) */
  utm_content: z.string().transform(sanitizeString).optional(),

  /** Whether the form content should be enriched with LeadIQ data */
  shouldEnrich: z.boolean().optional(),

  /** The tracking purposes returned from airgap.js (includes ConfigurablePurpose and any custom purposes) */
  //TODO: Find out why we can push a property not in "ConfigurablePurposes"
  trackingPurposes: z.record(z.string(), z.boolean()).optional(),

  /** An optional analytics ID for the form */
  analyticsId: z.string().transform(sanitizeString).optional(),

  /**
   * Full URL where the form was submitted (domain, path, and query string).
   * Set client-side from `window.location.href`.
   */
  conversionUrl: z
    .string()
    .max(2000, { error: maxLengthError('Conversion URL', 2000) })
    .url({ error: 'Conversion URL must be a valid URL.' })
    .transform(sanitizeString)
    .optional(),

  /** Whether the form is complete, allows for staging of multi-step forms */
  isSubmissionComplete: z
    .union([
      z.boolean(),
      z.enum(['true', 'false']).transform((v) => v === 'true'),
    ])
    .optional(),

  /**
   * Opt the submission into abandon forwarding after the grace period.
   * Set on /contact field submit (not homepage email capture).
   */
  forwardOnAbandon: z
    .union([
      z.boolean(),
      z.enum(['true', 'false']).transform((v) => v === 'true'),
    ])
    .optional(),
});
export type ContactFormMetadata = z.infer<typeof ContactFormMetadata>;

/**
 * The ultimate payload of the contact form sent via HTTP POST to the contact form handler.
 */
export const ContactFormBody = z.intersection(
  ContactFormFields,
  ContactFormMetadata,
);
export type ContactFormBody = z.infer<typeof ContactFormBody>;

export { isPersonalEmail, PERSONAL_EMAIL_DOMAINS } from './personalEmail';
